import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAppId } from '../../context/AppIdContext';
import Header from '../../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const AllTransactions = () => {
    const { appId, language } = useAppId();
    const navigate = useNavigate();

    const [transactions, setTransactions] = useState([]);
    const [filteredTransactions, setFilteredTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all'); // 'all', 'campaign', 'recharge', 'income'

    const lang = language || 'en';

    // --- Translation Dictionary ---
    const t = {
        en: {
            title: "All Transactions",
            subtitle: "Complete transaction history",
            search: "Search by Ref ID, Remarks, or User...",
            allTypes: "All Types",
            campaign: "Campaign",
            recharge: "Recharge",
            income: "Income",
            refId: "Ref ID",
            messages: "Messages",
            amount: "Amount",
            remarks: "Remarks",
            user: "User",
            date: "Date",
            loading: "Loading Transactions...",
            noTransactions: "No transactions found.",
            totalTransactions: "Total Transactions",
            totalAmount: "Total Amount",
            totalMessages: "Total Messages",
            backToWallet: "Back to Wallet"
        },
        hi: {
            title: "सभी लेनदेन",
            subtitle: "पूर्ण लेनदेन इतिहास",
            search: "संदर्भ आईडी, टिप्पणी या उपयोगकर्ता से खोजें...",
            allTypes: "सभी प्रकार",
            campaign: "कैंपेन",
            recharge: "रिचार्ज",
            income: "आय",
            refId: "संदर्भ आईडी",
            messages: "संदेश",
            amount: "राशि",
            remarks: "टिप्पणी",
            user: "उपयोगकर्ता",
            date: "दिनांक",
            loading: "लेनदेन लोड हो रहे हैं...",
            noTransactions: "कोई लेनदेन नहीं मिला।",
            totalTransactions: "कुल लेनदेन",
            totalAmount: "कुल राशि",
            totalMessages: "कुल संदेश",
            backToWallet: "वॉलेट पर वापस जाएं"
        },
        gu: {
            title: "બધા ટ્રાન્ઝેક્શન",
            subtitle: "સંપૂર્ણ ટ્રાન્ઝેક્શન ઇતિહાસ",
            search: "રેફ આઈડી, વિગત અથવા વપરાશકર્તા દ્વારા શોધો...",
            allTypes: "બધા પ્રકાર",
            campaign: "કેમ્પેઇન",
            recharge: "રિચાર્જ",
            income: "આવક",
            refId: "રેફ આઈડી",
            messages: "મેસેજ",
            amount: "રકમ",
            remarks: "વિગત",
            user: "વપરાશકર્તા",
            date: "તારીખ",
            loading: "ટ્રાન્ઝેક્શન લોડ થઈ રહ્યા છે...",
            noTransactions: "કોઈ ટ્રાન્ઝેક્શન મળ્યું નથી.",
            totalTransactions: "કુલ ટ્રાન્ઝેક્શન",
            totalAmount: "કુલ રકમ",
            totalMessages: "કુલ મેસેજ",
            backToWallet: "વોલેટ પર પાછા જાઓ"
        }
    };

    const txt = t[lang] || t.en;

    // --- Fetch All Transactions ---
    useEffect(() => {
        if (!appId) {
            navigate('/login');
            return;
        }

        const fetchTransactions = async () => {
            setLoading(true);
            try {
                const response = await axios.post(`${BASE_URL}/api/dis_wtsp_wallet_transaction`, {
                    app_id: appId
                });

                if (response.data.success && response.data.data) {
                    const result = response.data.data.result || [];
                    setTransactions(result);
                    setFilteredTransactions(result);
                }
            } catch (error) {
                console.error("Error fetching transactions:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchTransactions();
    }, [appId, navigate]);

    // --- Filter & Search Logic ---
    useEffect(() => {
        let filtered = transactions;

        // Filter by type
        if (filterType !== 'all') {
            filtered = filtered.filter(txn => 
                txn.txn_type?.toLowerCase() === filterType.toLowerCase()
            );
        }

        // Search filter
        if (searchTerm.trim() !== '') {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(txn =>
                txn.ref_id?.toLowerCase().includes(term) ||
                txn.remarks?.toLowerCase().includes(term) ||
                txn.user_name?.toLowerCase().includes(term)
            );
        }

        setFilteredTransactions(filtered);
    }, [searchTerm, filterType, transactions]);

    // --- Calculate Statistics ---
    const totalAmount = filteredTransactions.reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0);
    const totalMessages = filteredTransactions.reduce((sum, txn) => sum + (Number(txn.messages) || 0), 0);

    return (
        <div className="app-container p-4 custom-responsive-container">
            <Header title={txt.title} />

            {loading && (
                <div className="loader-wrapper">
                    <div className="spinner"></div>
                    <p className="loader-text">{txt.loading}</p>
                </div>
            )}

            {!loading && (
                <div className="transactions-page-content slide-up-element mt-3">
                    

                    {/* ================= FILTER TABS ================= */}
                    <div className="filter-tabs-wrapper">
                        <div className="filter-tabs">
                            <button 
                                className={`filter-tab ${filterType === 'all' ? 'active' : ''}`}
                                onClick={() => setFilterType('all')}
                            >
                                {txt.allTypes}
                            </button>
                            <button 
                                className={`filter-tab ${filterType === 'campaign' ? 'active' : ''}`}
                                onClick={() => setFilterType('campaign')}
                            >
                                {txt.campaign}
                            </button>
                            <button 
                                className={`filter-tab ${filterType === 'recharge' ? 'active' : ''}`}
                                onClick={() => setFilterType('recharge')}
                            >
                                {txt.recharge}
                            </button>
                        </div>
                    </div>

                    {/* ================= SEARCH SECTION ================= */}
                    <div className="search-section" style={{maxWidth:"450px"}}>
                        <div className="search-box">
                            <svg className="search-icon" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <input
                                type="text"
                                placeholder={txt.search}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="search-input"
                            />
                            {searchTerm && (
                                <button className="clear-search-btn" onClick={() => setSearchTerm('')}>
                                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            )}
                        </div>
                    </div>

                    {/* ================= TRANSACTIONS LIST ================= */}
                    {filteredTransactions.length === 0 ? (
                        <div className="empty-state-box">
                            <div className="empty-icon-wrap">
                                <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            <p className="empty-text">{txt.noTransactions}</p>
                        </div>
                    ) : (
                        <div className="txn-list-grid">
                            {filteredTransactions.map((txn, index) => {
                                const isCredit = txn.txn_type?.toLowerCase() === 'income' || txn.txn_type?.toLowerCase() === 'recharge';
                                const isCampaign = txn.txn_type?.toLowerCase() === 'campaign';
                                const handleCardClick = () => {
                                    if (!isCampaign) return;
                                    const campaign = {
                                        campaign_id: txn.campaign_id || txn.ref_id,
                                        total_message_use: txn.messages || txn.total_message_use || 0,
                                        total_cost: txn.amount || txn.total_cost || 0,
                                        total_logs: txn.total_logs || 0,
                                        create_by_name: txn.user_name || txn.create_by_name || 'N/A',
                                        remarks: txn.remarks || '',
                                        create_date: txn.create_date || '',
                                        send_date: txn.send_date || txn.create_date || ''
                                    };
                                    navigate('/dis_meta_bulk_campaign_wise_voter_list', {
                                        state: { campaign, campaign_id: campaign.campaign_id }
                                    });
                                };
                                return (
                                    <div
                                        key={txn.ref_id || index}
                                        className={`transaction-card ${isCampaign ? 'is-clickable' : ''}`}
                                        onClick={handleCardClick}
                                        style={{ cursor: isCampaign ? 'pointer' : 'default' }}
                                    >
                                        <div className="txn-card-left">
                                            <div className={`txn-arrow-avatar ${isCredit ? 'txn-arrow-credit' : 'txn-arrow-debit'}`}>
                                                {isCredit ? (
                                                    <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                                                    </svg>
                                                ) : (
                                                    <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 10l7-7m0 0l7 7m-7-7v18" />
                                                    </svg>
                                                )}
                                            </div>

                                            <div className="txn-card-details">
                                                <div className="txn-card-title">{txn.messages} SMS</div>
                                                <div className="txn-card-subtitle">
                                                    {txn.create_date} <span className="dot-sep">·</span> {txn.txn_type}
                                                </div>
                                                <div className="txn-card-refid">{txn.ref_id}</div>
                                            </div>
                                        </div>

                                        <div className="txn-card-amount-right">
                                            <div className={`txn-card-amount ${isCredit ? 'credit' : 'debit'}`}>
                                                {txn.txn_action === 'ADD' ? '+' : '-'}₹{Number(txn.amount || 0).toFixed(2)}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            <style jsx>{`
                /* ── BASE ── */
                .app-container {
                    background: #F4F6F9;
                    min-height: 100vh;
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
                    -webkit-font-smoothing: antialiased;
                }

                /* ── ANIMATIONS ── */
                @keyframes slideUp {
                    from { opacity: 0; transform: translateY(15px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                @keyframes spin {
                    0%   { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                .slide-up-element {
                    opacity: 0;
                    animation: slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                }

                /* ── LOADER ── */
                .loader-wrapper {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    min-height: 60vh;
                    gap: 14px;
                }
                .spinner {
                    width: 44px;
                    height: 44px;
                    border: 3px solid #E3E8F0;
                    border-top: 3px solid #00A67E;
                    border-radius: 50%;
                    animation: spin 0.85s linear infinite;
                }
                .loader-text {
                    color: #8A96A8;
                    font-weight: 600;
                    font-size: 13.5px;
                }

                /* ── STATISTICS GRID ── */
                .stats-grid {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 16px;
                    margin-bottom: 24px;
                }
                .stat-card {
                    background: #FFFFFF;
                    border-radius: 18px;
                    padding: 20px 24px;
                    border: 1px solid #E3E8F0;
                    display: flex;
                    align-items: center;
                    gap: 18px;
                    box-shadow: 0 2px 8px rgba(15,23,42,0.04);
                    transition: all 0.22s ease;
                }
                .stat-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 12px 28px rgba(15,23,42,0.08);
                }
                .stat-icon {
                    width: 52px;
                    height: 52px;
                    border-radius: 14px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }
                .stat-card-1 .stat-icon {
                    background: linear-gradient(135deg, #EEF4FF, #C7D9FF);
                    color: #1A4BD1;
                }
                .stat-card-2 .stat-icon {
                    background: linear-gradient(135deg, #DCFDF0, #A7F3D8);
                    color: #047857;
                }
                .stat-card-3 .stat-icon {
                    background: linear-gradient(135deg, #FFF8E7, #FDEAAA);
                    color: #92650A;
                }
                .stat-info {
                    flex: 1;
                }
                .stat-label {
                    font-size: 12px;
                    font-weight: 600;
                    color: #8A96A8;
                    margin-bottom: 4px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }
                .stat-value {
                    font-size: 24px;
                    font-weight: 900;
                    color: #0D1321;
                    letter-spacing: -0.5px;
                }

                /* ── FILTER TABS ── */
                .filter-tabs-wrapper {
                    margin-bottom: 20px;
                }
                .filter-tabs {
                    display: flex;
                    gap: 10px;
                    flex-wrap: wrap;
                }
                .filter-tab {
                    padding: 10px 22px;
                    border-radius: 12px;
                    border: 1px solid #E3E8F0;
                    background: #FFFFFF;
                    font-size: 13px;
                    font-weight: 700;
                    color: #4A5568;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    box-shadow: 0 1px 3px rgba(15,23,42,0.04);
                }
                .filter-tab:hover {
                    background: #F1F4F9;
                    border-color: #C8D0DF;
                    transform: translateY(-1px);
                }
                .filter-tab.active {
                    background: #00A67E;
                    border-color: #00A67E;
                    color: #FFFFFF;
                    box-shadow: 0 4px 14px rgba(0, 166, 126, 0.25);
                }

                /* ── SEARCH SECTION ── */
                .search-section {
                    margin-bottom: 24px;
                }
                .search-box {
                    position: relative;
                }
                .search-icon {
                    position: absolute;
                    left: 16px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #8A96A8;
                    pointer-events: none;
                }
                .search-input {
                    width: 100%;
                    padding: 15px 48px 15px 48px;
                    border: 1px solid #E3E8F0;
                    border-radius: 14px;
                    font-size: 14px;
                    font-weight: 500;
                    color: #0D1321;
                    background: #FFFFFF;
                    transition: all 0.2s ease;
                    box-shadow: 0 2px 8px rgba(15,23,42,0.04);
                }
                .search-input:focus {
                    outline: none;
                    border-color: #00A67E;
                    box-shadow: 0 0 0 4px rgba(0, 166, 126, 0.08);
                }
                .search-input::placeholder {
                    color: #B0BCCF;
                }
                .clear-search-btn {
                    position: absolute;
                    right: 14px;
                    top: 50%;
                    transform: translateY(-50%);
                    background: #E3E8F0;
                    border: none;
                    border-radius: 8px;
                    width: 30px;
                    height: 30px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    color: #4A5568;
                    transition: all 0.2s ease;
                }
                .clear-search-btn:hover {
                    background: #C8D0DF;
                }

                /* ── EMPTY STATE ── */
                .empty-state-box {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 16px;
                    padding: 60px 20px;
                }
                .empty-icon-wrap {
                    width: 72px;
                    height: 72px;
                    border-radius: 20px;
                    background: #FFFFFF;
                    border: 1px solid #E3E8F0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #B0BCCF;
                }
                .empty-text {
                    font-size: 14.5px;
                    font-weight: 600;
                    color: #8A96A8;
                }

                /* ── TRANSACTION CARDS GRID ── */
                .txn-list-grid {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 14px;
                }

                .transaction-card {
                    background: #FFFFFF;
                    border-radius: 18px;
                    padding: 16px 18px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    border: 1px solid #E8EEF4;
                    box-shadow: 0 4px 14px rgba(15, 23, 42, 0.04);
                    transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;
                    gap: 14px;
                    position: relative;
                    overflow: hidden;
                }
                .transaction-card::before {
                    content: '';
                    position: absolute;
                    left: 0;
                    top: 0;
                    bottom: 0;
                    width: 4px;
                    background: linear-gradient(180deg, #0f2b3c 0%, #11816A 100%);
                    opacity: 0.85;
                }
                .transaction-card:hover {
                    box-shadow: 0 14px 28px rgba(17, 129, 106, 0.12);
                    border-color: #cfe5dc;
                    transform: translateY(-4px);
                }
                .transaction-card.is-clickable:active {
                    transform: translateY(-1px);
                }

                .txn-card-left {
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    flex: 1;
                    min-width: 0;
                    padding-left: 4px;
                }

                .txn-arrow-avatar {
                    width: 48px;
                    height: 48px;
                    border-radius: 14px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    box-shadow: inset 0 1px 0 rgba(255,255,255,0.55);
                }
                .txn-arrow-debit {
                    background: linear-gradient(135deg, #FDECEC 0%, #FEE2E2 100%);
                    color: #DC2626;
                    border: 1px solid #fecaca;
                }
                .txn-arrow-credit {
                    background: linear-gradient(135deg, #E0EFEB 0%, #D1FAE5 100%);
                    color: #11816A;
                    border: 1px solid #cfe5dc;
                }

                .txn-card-details {
                    display: flex;
                    flex-direction: column;
                    min-width: 0;
                    gap: 5px;
                }
                .txn-card-title {
                    font-size: 15.5px;
                    font-weight: 800;
                    color: #0f2b3c;
                    letter-spacing: -0.2px;
                    line-height: 1.25;
                }
                .txn-card-subtitle {
                    font-size: 12.5px;
                    font-weight: 600;
                    color: #6B7280;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                .dot-sep {
                    margin: 0 4px;
                    color: #B0BCCF;
                }
                .txn-card-refid {
                    display: inline-block;
                    max-width: 100%;
                    font-size: 11px;
                    font-weight: 600;
                    color: #64748b;
                    background: #F4F7FA;
                    border: 1px solid #E8EEF4;
                    border-radius: 8px;
                    padding: 3px 8px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .txn-card-amount-right {
                    flex-shrink: 0;
                    display: flex;
                    flex-direction: column;
                    align-items: flex-end;
                    gap: 6px;
                }
                .txn-card-amount {
                    font-size: 16.5px;
                    font-weight: 800;
                    letter-spacing: -0.3px;
                    white-space: nowrap;
                    padding: 7px 12px;
                    border-radius: 12px;
                }
                .txn-card-amount.debit {
                    color: #DC2626;
                    background: #FEF2F2;
                    border: 1px solid #fecaca;
                }
                .txn-card-amount.credit {
                    color: #047857;
                    background: #ECFDF5;
                    border: 1px solid #a7f3d0;
                }

                /* ── RESPONSIVE ── */
                @media (max-width: 1024px) {
                    .txn-list-grid {
                        grid-template-columns: repeat(2, 1fr);
                    }
                }
                @media (max-width: 992px) {
                    .stats-grid {
                        grid-template-columns: repeat(2, 1fr);
                    }
                }
                @media (max-width: 768px) {
                    .stats-grid {
                        grid-template-columns: 1fr;
                    }
                    .stat-value {
                        font-size: 20px;
                    }
                    .txn-list-grid {
                        grid-template-columns: 1fr;
                    }
                    .filter-tabs {
                        gap: 8px;
                    }
                    .filter-tab {
                        padding: 8px 16px;
                        font-size: 12px;
                        flex: 1;
                        text-align: center;
                    }
                }
                @media (max-width: 480px) {
                    .transaction-card {
                        padding: 14px 14px 14px 16px;
                    }
                    .txn-arrow-avatar {
                        width: 42px;
                        height: 42px;
                        border-radius: 12px;
                    }
                    .txn-card-title {
                        font-size: 14px;
                    }
                    .txn-card-amount {
                        font-size: 13.5px;
                        padding: 6px 10px;
                    }
                    .stat-card {
                        padding: 16px 20px;
                    }
                }
            `}</style>
        </div>
    );
};

export default AllTransactions;